/**
 * Supplier Performance Page
 *
 * Refactored to use modular sub-components for improved maintainability.
 * All state management is handled by the useSupplierPerformance hook.
 */

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Search, Grid3X3, Table, Loader2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import SupplierPerformanceFilters from '../components/suppliers/SupplierPerformanceFilters';
import SupplierSelfPerformanceFilters from '../components/suppliers/SupplierSelfPerformanceFilters';
import SupplierPerformanceMatrixView from '../components/suppliers/SupplierPerformanceMatrixView';
import OTIFReviewTab from '../components/purchasing/OTIFReviewTab';
import {
  useSupplierPerformance,
  DrilldownModal,
  PerformanceTable,
} from './supplier-performance';
import { usePerformanceMatrix } from './supplier-performance/matrix/usePerformanceMatrix';
import { usePerformanceExport } from './supplier-performance/usePerformanceExport';

export default function SupplierPerformance() {
  const {
    // Data
    filteredSuppliers,
    ratingThresholds,
    // SI-16: supplier-side page (own scores, one row per customer)
    isSupplier,
    selfOptions,

    // UI states
    loading,
    initialLoad,
    error,
    searchTerm,
    isMatrixView,
    filters,

    // Pagination
    pagination,

    // Modal
    modalOpen,
    modalTitle,
    modalSubtitle,
    modalData,
    modalType,
    modalLoading,
    modalError,
    modalPage,
    modalTotalCount,
    modalTotalPages,
    modalContext,
    modalExporting,
    handleModalExport,

    // Currency
    currency,

    // Setters
    setSearchTerm,
    setModalOpen,

    // Actions
    fetchData,
    handleApplyFilters,
    handleExportExcel,
    handleMatrixView,
    handleModalPageChange,
    openGRNModal,
    openNCRModal,
    openPOLineModal,
    buildSiteRowParams,
  } = useSupplierPerformance();

  // Customer review 11 Sep (item 18): the matrix's data lives on the page
  // so "Export to Excel" exports the view on screen — in Matrix view it used
  // to write the table.
  const matrix = usePerformanceMatrix(
    {
      data_mode: filters.dataMode,
      is_preferred: filters.strategic,
      warehouse: filters.warehouse,
      exclude_na_ratings: filters.excludeNARatings,
      // CR-7264: the Supplier Group filter reaches the matrix too.
      company__group: filters.supplierGroup,
      // CR-7264 rework: so does the Company Site filter.
      site: filters.site,
      // CR-6312 rework: the Table's Supplier Site filter (company_id — the
      // supplier company) narrows the matrix too.
      company_id: filters.supplierSite,
    },
    isMatrixView && !isSupplier,
    // CR-7297: the quick search narrows the grid (and so its export) too.
    searchTerm,
  );
  // CR-7297: one Export control for both views — Table writes every supplier
  // matching the filters, Matrix writes the grid on screen.
  const exportControl = usePerformanceExport({
    isMatrixView: isMatrixView && !isSupplier,
    matrix,
    exportTable: handleExportExcel,
    tableCount: pagination.totalCount,
    tableLoading: loading,
  });

  // CR-6191: tabbed view — the existing performance matrix + a new OTIF Review
  // sub-tab. The daily OTIF email deep-links here with ?tab=otif-review.
  const [searchParams, setSearchParams] = useSearchParams();
  // SI-16: OTIF Review is a customer's review of its suppliers — not shown on
  // the supplier-side page.
  const activeTab = !isSupplier && searchParams.get('tab') === 'otif-review' ? 'otif-review' : 'performance';
  const setActiveTab = (tab: string) => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'performance') next.delete('tab');
    else next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  if (loading && initialLoad) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-sm text-muted-foreground">Loading supplier performance data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error: {error}</p>
          <button
            onClick={fetchData}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Supplier Performance</h1>
          <p className="text-muted-foreground mt-2">
            {isSupplier
              ? 'How each customer rates your deliveries and quality, one row per customer site'
              : 'Comprehensive supplier performance metrics and analytics'}
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          {!isSupplier && <TabsTrigger value="otif-review">OTIF Review</TabsTrigger>}
        </TabsList>

        <TabsContent value="performance" className="space-y-6">
      {/* Comprehensive Filter Panel. SI-16: the supplier-side page gets the
          customer-oriented panel (customer group / site, dates, rating). */}
      {isSupplier ? (
        <SupplierSelfPerformanceFilters
          filters={filters}
          options={selfOptions}
          onApplyFilters={handleApplyFilters}
          onRefresh={fetchData}
          onExport={exportControl.onExport}
          exporting={exportControl.exporting}
          exportDisabled={exportControl.disabled}
          exportTitle={exportControl.title}
        />
      ) : (
        <SupplierPerformanceFilters
          filters={filters}
          ratingThresholds={ratingThresholds}
          onApplyFilters={handleApplyFilters}
          // CR-6403: the design places Refresh + Export in the filter header.
          onRefresh={fetchData}
          // Customer review 11 Sep (msg 18) / CR-7297: the page's only Export;
          // it writes the view on screen.
          onExport={exportControl.onExport}
          exporting={exportControl.exporting}
          exportDisabled={exportControl.disabled}
          exportTitle={exportControl.title}
        />
      )}

      {/* Search and Action Bar */}
      <div className="flex items-center justify-between gap-4">
        {/* Quick Search */}
        <div className="flex-1 relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={isSupplier ? 'Search by customer name...' : 'Search by supplier name...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* View actions. CR-7297: Export lives in the filter bar only — the
            second "Export to Excel" that sat here is gone. */}
        <div className="flex items-center gap-2">
          {/* Rows per page selector. CR-7297: the Table's pager only — the
              matrix is one unpaged grid, so the picker did nothing there. */}
          {!isMatrixView && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap">Rows:</span>
              <Select
                value={pagination.pageSize.toString()}
                onValueChange={(value) => {
                  pagination.setPageSize(parseInt(value));
                }}
              >
                <SelectTrigger className="w-[70px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {/* SI-16: no Matrix View on the supplier-side page — the matrix pools
              customers, and legacy hid it for suppliers too (CR-6301). */}
          {!isSupplier && (
            <Button
              variant={isMatrixView ? 'default' : 'outline'}
              size="sm"
              onClick={handleMatrixView}
            >
              {isMatrixView ? (
                <>
                  <Table className="h-4 w-4 mr-2" />
                  Table View
                </>
              ) : (
                <>
                  <Grid3X3 className="h-4 w-4 mr-2" />
                  Matrix View
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Performance Table or Matrix View */}
      {isMatrixView ? (
        // CR-6312: the matrix honours the page's filters (usePerformanceMatrix above).
        <SupplierPerformanceMatrixView matrix={matrix} />
      ) : (
        <PerformanceTable
          suppliers={filteredSuppliers}
          ratingThresholds={ratingThresholds}
          currency={currency}
          loading={loading}
          viewerIsSupplier={isSupplier}
          showSupplierSiteColumn={selfOptions.supplierSites.length > 1}
          // CR-7264: the group total is shown only once the table has been
          // filtered to a group — that is when the user is asking about the
          // group rather than ranking suppliers against each other.
          groupFilterActive={filters.supplierGroup !== 'all'}
          // Customer review 11 Sep (msg 3/4): per-customer-site breakdown rows.
          buildSiteRowParams={buildSiteRowParams}
          pagination={{
            currentPage: pagination.currentPage,
            totalPages: pagination.totalPages,
            totalCount: pagination.totalCount,
            pageSize: pagination.pageSize,
            hasMultiplePages: pagination.hasMultiplePages,
            goToPage: pagination.goToPage,
          }}
          onOpenGRNModal={openGRNModal}
          onOpenNCRModal={openNCRModal}
          onOpenPOLineModal={openPOLineModal}
        />
      )}

      {/* Drilldown Modal */}
      <DrilldownModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={modalTitle}
        subtitle={modalSubtitle}
        type={modalType}
        filterType={modalContext?.filterType}
        metric={modalContext?.metric}
        isSupplier={isSupplier}
        data={modalData}
        loading={modalLoading}
        error={modalError}
        page={modalPage}
        totalCount={modalTotalCount}
        totalPages={modalTotalPages}
        currency={currency}
        onPageChange={handleModalPageChange}
        onExport={handleModalExport}
        exporting={modalExporting}
      />
        </TabsContent>

        <TabsContent value="otif-review">
          <OTIFReviewTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
